import React, { useEffect } from "react"
import { Link, graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import parse from "html-react-parser"

import Layout from "../components/layout"
import { SEO } from "../components/seo"

import "../css/blog-post.css"

function extractTextFromHtml(htmlInput) {
  if (typeof window !== "undefined") {
    // Create a temporary div element
    let tempDiv = document.createElement("div")
    tempDiv.id = "tempDivId"
    tempDiv.innerHTML = htmlInput

    // Get the text content
    let textContent = tempDiv.textContent || tempDiv.innerText

    // Remove the temporary div by its id
    let tempDivToRemove = document.getElementById("tempDivId")
    if (tempDivToRemove) {
      tempDivToRemove.parentNode.removeChild(tempDivToRemove)
    }

    // Return the extracted text
    return textContent
  }
  return htmlInput
}

const BlogPostTemplate = ({ data: { previous, next, post } }) => {
  const featuredImage = {
    image:
      post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData,
    alt: post.featuredImage?.node?.altText || ``,
  }

  console.log(post.content)

  useEffect(() => {
    if (typeof window !== "undefined") {
      // find all a tags inside articleBody
      const articleBody = document.querySelector(".articleBody")
      const aTags = articleBody.querySelectorAll("a")

      // loop through all a tags
      aTags.forEach(a => {
        // add target blank
        a.setAttribute("target", "_blank")

        // add rel noopener noreferrer
        a.setAttribute("rel", "noopener noreferrer")

        // append ArrowUpRight svg
        a.innerHTML += `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 3V4H11.295L3 12.295L3.705 13L12 4.705V11H13V3H5Z" fill="white"/>
        </svg>
        `
      })
    }
  }, [typeof window])
  return (
    <Layout>
      <article
        className="blog-post"
        itemScope
        itemType="http://schema.org/Article"
      >
        <header>
          <h1 itemProp="headline">{parse(post.title)}</h1>

          <p>{post.date}</p>

          {/* if we have a featured image for this post let's display it */}
          {featuredImage?.image && (
            <GatsbyImage
              alt={featuredImage.alt}
              image={featuredImage.image}
              loading="eager"
              placeholder="blurred"
              className="image"
            />
          )}
          <div className="rect"></div>
        </header>

        {!!post.content && (
          <section itemProp="articleBody" className="articleBody">
            {parse(post.content)}
          </section>
        )}

        <hr />
      </article>

      <div className="pagination">
        {previous && (
          <Link
            to={`/blog${previous.uri}`}
            rel="prev"
            className="page previous"
          >
            ← {parse(previous.title)}
          </Link>
        )}
        {next && (
          <Link to={`/blog${next.uri}`} rel="next" className="page next">
            {parse(next.title)} →
          </Link>
        )}
      </div>
    </Layout>
  )
}

export const Head = ({ data: { post } }) => {
  const schemaOrgPost = {
    "@context": "http://schema.org",
    "@type": "Article",
    articleBody: extractTextFromHtml(post.content),
    headline: post.title,
    datePublished: post.date,
    about: post.title,
    abstract: post.excerpt,
    genre: "travel",
    keywords: `amalgam, villas, luxury, stays, rental, vacation, travel, blog, island, 
    naxos, tinos, mykonos, paros, greece, naxos greece, naxos island, paros greece, paros island, tinos greece, tinos island, mykonos greece, mykonos island, 
    greek islands, greek island hopping
    greece villas with pool, greece luxury villas, greek islands, villas for rent in greece, villas for rent in cyclades, villas for rent in greece with pool`,
  }

  // create structured data for SEO
  // breadcrumbs
  // https://developers.google.com/search/docs/data-types/breadcrumb
  // https://schema.org/BreadcrumbList
  // https://schema.org/Breadcrumb
  // https://schema.org/ListItem
  // https://schema.org/ImageObject
  // https://schema.org/Thing

  const schemaOrgBreadcrumb = {
    "@context": "http://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@id": "https://www.amalgamhomes.com/",
          name: "Amalgam Homes",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: {
          "@id": "https://www.amalgamhomes.com/blog",
          name: "Blog",
        },
      },
      {
        "@type": "ListItem",
        position: 3,
        item: {
          "@id": `https://www.amalgamhomes.com/blog${post.uri}`,
          name: post.title,
        },
      },
    ],
  }

  const schemaOrgImage = {
    "@context": "http://schema.org",
    "@type": "ImageObject",
    url: post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
      .images.fallback.src,
    width:
      post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
        .width,
    height:
      post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
        .height,
    caption: post.featuredImage?.node?.altText,
  }

  const schemaOrgThing = {
    "@context": "http://schema.org",
    "@type": "Thing",
    name: post.title,
    description: post.excerpt,
    image:
      post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
        .images.fallback.src,
  }

  return (
    <SEO
      title={post.title}
      description={post.excerpt}
      image={
        post.featuredImage?.node?.localFile?.childImageSharp?.gatsbyImageData
      }
    >
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgPost)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgBreadcrumb)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgImage)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgThing)}
      </script>
    </SEO>
  )
}
export default BlogPostTemplate

export const pageQuery = graphql`
  query BlogPostById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
    $previousPostId: String
    $nextPostId: String
  ) {
    # selecting the current post by id
    post: wpPost(id: { eq: $id }) {
      id
      uri
      excerpt
      content
      title
      date(formatString: "MMMM DD, YYYY")
      featuredImage {
        node {
          altText
          localFile {
            childImageSharp {
              gatsbyImageData(
                layout: FULL_WIDTH
                quality: 90
                formats: [AUTO, JPG, WEBP]
              )
            }
          }
        }
      }
    }
    # this gets us the previous post by id (if it exists)
    previous: wpPost(id: { eq: $previousPostId }) {
      uri
      title
    }
    # this gets us the next post by id (if it exists)
    next: wpPost(id: { eq: $nextPostId }) {
      uri
      title
    }
  }
`
